import { AlertDialog } from "@choose/ui/alert-dialog";
import { Button } from "@choose/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@choose/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@choose/ui/dropdown-menu";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@choose/ui/tabs";
import { Tooltip, TooltipContent, TooltipTrigger } from "@choose/ui/tooltip";
import { SiAndroid, SiApple } from "@icons-pack/react-simple-icons";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import parsePhoneNumberFromString from "libphonenumber-js";
import {
  BookSearchIcon,
  CandyOffIcon,
  CopyIcon,
  CrownIcon,
  EllipsisIcon,
  HistoryIcon,
  MinusIcon,
  PackageSearchIcon,
  PlusIcon,
  ServerCrashIcon,
  SquarePenIcon,
  TriangleAlertIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AddCreditDialog } from "~/components/common/contact/add-credit-dialog";
import { AddCreditViaReferralDialog } from "~/components/common/contact/add-credit-via-referral-dialog";
import { FraudsterBadge } from "~/components/common/contact/fraudster-badge";
import { InfluencerBadge } from "~/components/common/contact/influencer-badge";
import { MarkAsFraudsterDialog } from "~/components/common/contact/mark-as-fraudster-dialog";
import { MarkAsInfluencerDialog } from "~/components/common/contact/mark-as-influencer-dialog";
import { UnmarkAsFraudsterDialog } from "~/components/common/contact/unmark-as-fraudster-dialog";
import { UnmarkAsInfluencerDialog } from "~/components/common/contact/unmark-as-influencer-dialog";
import { ContactActivityTrigger } from "~/components/common/contacts/contact-activity-trigger";
import { getContactNotesQueryKey } from "~/components/common/contacts/contact-timeline";
import { EditReferralCodeDialog } from "~/components/common/order/edit-referral-code-dialog";
import { RemoveCreditDialog } from "~/components/common/order/remove-credit-dialog";
import {
  DEFAULT_COLUMN_VISIBILITY as ORDER_DEFAULT_COLUMN_VISIBILITY,
  OrderList,
  SELECTABLE_TABLE_DEFAULT_STATE,
} from "~/components/common/orders/order-list";
import { CreateTicketDialog } from "~/components/common/ticket/create-ticket-dialog";
import { Address } from "~/components/common/ticket/timeline/address";
import {
  TABLE_ACCESSOR_CONTACT,
  TABLE_ACCESSOR_SUB_STATUS,
  DEFAULT_COLUMN_VISIBILITY as TICKET_DEFAULT_COLUMN_VISIBILITY,
  TicketList,
} from "~/components/common/tickets/ticket-list";
import { CreditSummaryDialog } from "~/components/layout/sidebars/ticket-detail-sidebar/credit-summary-dialog";
import { Copy } from "~/components/ui/copy";
import { StoreIcon } from "~/components/ui/icons/store-icon";
import { RelativeTime } from "~/components/ui/relative-time";
import { Time } from "~/components/ui/time";
import type { ContactQuery, Orders_Search_Input } from "~/graphql/graphql.gen";
import {
  Orders_Search_Sort_Field_Input,
  Orders_Search_Sort_Order_Input,
  Store,
  SupportAppContactMobileDeviceOs,
  SupportTicketsSortableDirection,
  SupportTicketsSortableFields,
  useContactQuery,
  useCurrentUserQuery,
  useInfiniteContactActivitiesQuery,
  useInfiniteSearchOrdersQuery,
  useInfiniteTicketsQuery,
} from "~/graphql/graphql.gen";
import { useFormatAddressQuery } from "~/hooks/use-format-address-query";
import { useLanguage } from "~/hooks/use-language";
import { useRegion } from "~/hooks/use-region";
import { toLocale } from "~/libs/locale";
import { formatCurrency } from "~/libs/number";
import { getContactFullName } from "~/stores/contacts-store";
import { countFilters, useQueueStore } from "~/stores/queue-store";
import { AGENT_OR_MARKETING_ROLES, AGENT_ROLES } from "~/stores/roles-store";

enum Dialogs {
  AddCredit,
  AddCreditViaReferral,
  CreateTicket,
  EditReferralCode,
  MarkAsFraudster,
  MarkAsInfluencer,
  RemoveCredit,
  UnmarkAsFraudster,
  UnmarkAsInfluencer,
}

type AppContact = Extract<
  NonNullable<ContactQuery["supportActorById"]>,
  { __typename?: "SupportActorAppContact" }
>["appContact"];

const LatestShippingAddressInfo = (
  address: NonNullable<AppContact["latestShippingAddress"]>,
) => {
  const { i18n, t } = useTranslation();
  const { data: shippingFormattedAddress } = useFormatAddressQuery({
    address: {
      firstName: address.firstName,
      lastName: address.lastName,
      address1: address.street,
      address2: address.streetAdditional ?? "",
      city: address.city,
      country: address.countryCode,
      phone: address.phoneNumber,
      province: address.state ?? undefined,
      zip: address.zipCode,
    },
    locale: toLocale(i18n.language),
  });
  return (
    <div className="flex items-start">
      <Copy
        content={
          shippingFormattedAddress
            ?.filter((field) => field !== "")
            .join("\n") ?? ""
        }
        className="text-muted-foreground line-clamp-3 shrink-0 basis-[92px] text-left text-sm"
      >
        {t("ticket.contact_detail.last_shipping_address")}
      </Copy>
      <div className="flex min-h-8 flex-col px-3">
        <Address address={address} />
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/contact/$contactId",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { i18n, t } = useTranslation();
  const { contactId } = Route.useParams();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState<Dialogs | undefined>(undefined);
  const { setQueue } = useQueueStore();
  const { formatter: languageFormatter } = useLanguage(toLocale(i18n.language));
  const { formatter: regionFormatter } = useRegion(toLocale(i18n.language));
  const { data: user } = useCurrentUserQuery(undefined, {
    select: (data) => data.currentUser,
  });
  const {
    data: contact,
    isLoading,
    error,
  } = useContactQuery(
    {
      id: contactId,
    },
    {
      select: (data) => data.supportActorById,
    },
  );

  useEffect(() => {
    const filters = [
      {
        id: TABLE_ACCESSOR_CONTACT,
        value: [contactId],
      },
    ];

    const sorting = [
      {
        id: TABLE_ACCESSOR_SUB_STATUS,
        desc: false,
      },
    ];

    setQueue({
      display: {
        filterCount: countFilters({
          currentFilters: filters,
          referenceFilters: [],
        }),
        subTitle: t("ticket.status.todo"),
        title: t("app_sidebar.tickets"),
      },
      root: {
        page: {
          to: Route.fullPath,
          params: { contactId },
        },
      },
      state: {
        columnFilters: filters,
        sorting,
      },
    });
  }, [contactId, setQueue, t]);

  const refreshContact = () => {
    void queryClient.invalidateQueries({
      queryKey: useContactQuery.getKey({
        id: contactId,
      }),
    });
    void queryClient.invalidateQueries({
      queryKey: useInfiniteContactActivitiesQuery.getKey(
        getContactNotesQueryKey(contactId),
      ),
    });
  };

  if (isLoading) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col items-center justify-center">
              <Empty className="min-h-[300px] w-full max-w-md">
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Spinner className="size-4" />
                  </EmptyMedia>
                  <EmptyTitle>{t("contact.loading.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("contact.loading.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  if (
    !contact ||
    (error as Error | undefined)?.message === "contact_not_found" ||
    contact.__typename === "ActorBotUser" ||
    contact.__typename === "ActorInternalUser" ||
    contact.__typename === "ActorSupplierUser"
  )
    return (
      <>
        <title>{`${t("contact.not_found.title")} - ${t("app_name")}`}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full justify-center overflow-hidden">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CandyOffIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("contact.not_found.title")}</EmptyTitle>
                  <EmptyDescription>
                    {t("contact.not_found.description")}
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );

  if (error) {
    return (
      <>
        <title>{t("app_name")}</title>

        <main className="size-full overflow-hidden md:p-2">
          <div className="bg-container flex size-full overflow-hidden md:rounded-md md:border">
            <div className="bg-background flex size-full flex-col justify-center">
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <ServerCrashIcon />
                  </EmptyMedia>
                  <EmptyTitle>{t("contact.error")}</EmptyTitle>
                </EmptyHeader>
              </Empty>
            </div>
          </div>
        </main>
      </>
    );
  }

  const primaryCredits =
    contact.__typename === "SupportActorAppContact"
      ? contact.appContact.credits.find(
          (credit) => credit.store === contact.appContact.store,
        )
      : undefined;
  const secondaryCredits =
    contact.__typename === "SupportActorAppContact"
      ? contact.appContact.credits.find(
          (credit) => credit.store !== contact.appContact.store,
        )
      : undefined;

  const hasAgentRights =
    user?.__typename === "ActorInternalUser" &&
    AGENT_ROLES.includes(user.internalUser.supportRole);
  const hasAgentOrMarketingRights =
    user?.__typename === "ActorInternalUser" &&
    AGENT_OR_MARKETING_ROLES.includes(user.internalUser.supportRole);

  const traits = [
    contact.__typename === "SupportActorAppContact" &&
    contact.appContact.isFrauder
      ? {
          id: "fraudster",
          content: () => <FraudsterBadge />,
        }
      : null,
    contact.__typename === "SupportActorAppContact" &&
    contact.appContact.isInfluencer
      ? {
          id: "influencer",
          content: () => <InfluencerBadge />,
        }
      : null,
  ].filter((trait) => trait !== null);

  return (
    <>
      <title>{`${getContactFullName(contact)} - ${t("app_name")}`}</title>

      <div className="flex w-full flex-col md:flex-row">
        <div className="bg-background mb-1 w-full shrink-0 rounded-lg border px-4 py-6 md:mr-1 md:mb-0 md:w-[356px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span>{getContactFullName(contact)}</span>
              <ContactActivityTrigger contact={contact} />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="ghost" size="sm">
                    <EllipsisIcon />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="min-w-3xs">
                <DropdownMenuItem
                  className="gap-2"
                  onClick={() => setDialogOpen(Dialogs.CreateTicket)}
                  disabled={!hasAgentRights}
                >
                  <SquarePenIcon className="text-muted-foreground size-4" />
                  <span>{t("create_ticket_dialog.title")}</span>
                </DropdownMenuItem>
                {contact.__typename === "SupportActorAppContact" ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuLabel>
                        {t("contact.actions.credits.title")}
                      </DropdownMenuLabel>

                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => setDialogOpen(Dialogs.AddCredit)}
                        disabled={!hasAgentOrMarketingRights}
                      >
                        <PlusIcon className="text-muted-foreground size-4" />
                        <span>
                          {t("contact.actions.credits.add_credit.label")}
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() =>
                          setDialogOpen(Dialogs.AddCreditViaReferral)
                        }
                        disabled={!hasAgentOrMarketingRights}
                      >
                        <PlusIcon className="text-muted-foreground size-4" />
                        <span>
                          {t(
                            "contact.actions.credits.add_credit_via_referral.label",
                          )}
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => setDialogOpen(Dialogs.RemoveCredit)}
                        disabled={!hasAgentOrMarketingRights}
                      >
                        <MinusIcon className="text-muted-foreground size-4" />
                        <span>
                          {t("contact.actions.credits.remove_credit.label")}
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuLabel>
                        {t("contact.actions.referral.title")}
                      </DropdownMenuLabel>
                      <DropdownMenuItem
                        className="gap-2"
                        onClick={() => setDialogOpen(Dialogs.EditReferralCode)}
                        disabled={!hasAgentOrMarketingRights}
                      >
                        <SquarePenIcon className="text-muted-foreground size-4" />
                        <span>
                          {t(
                            "contact.actions.referral.edit_referral_code.label",
                          )}
                        </span>
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <DropdownMenuLabel>
                        {t("contact.actions.status.title")}
                      </DropdownMenuLabel>
                      {contact.appContact.isFrauder ? (
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() =>
                            setDialogOpen(Dialogs.UnmarkAsFraudster)
                          }
                          disabled={!hasAgentOrMarketingRights}
                        >
                          <TriangleAlertIcon className="text-muted-foreground size-4" />
                          <span>
                            {t(
                              "contact.actions.status.unmark_as_fraudster.label",
                            )}
                          </span>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() => setDialogOpen(Dialogs.MarkAsFraudster)}
                          disabled={!hasAgentOrMarketingRights}
                        >
                          <TriangleAlertIcon className="text-muted-foreground size-4" />
                          <span>
                            {t(
                              "contact.actions.status.mark_as_fraudster.label",
                            )}
                          </span>
                        </DropdownMenuItem>
                      )}
                      {contact.appContact.isInfluencer ? (
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() =>
                            setDialogOpen(Dialogs.UnmarkAsInfluencer)
                          }
                          disabled={!hasAgentOrMarketingRights}
                        >
                          <CrownIcon className="text-muted-foreground size-4" />
                          <span>
                            {t(
                              "contact.actions.status.unmark_as_influencer.label",
                            )}
                          </span>
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          className="gap-2"
                          onClick={() =>
                            setDialogOpen(Dialogs.MarkAsInfluencer)
                          }
                          disabled={!hasAgentOrMarketingRights}
                        >
                          <CrownIcon className="text-muted-foreground size-4" />
                          <span>
                            {t(
                              "contact.actions.status.mark_as_influencer.label",
                            )}
                          </span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                    <Copy
                      content={contact.id}
                      render={
                        <DropdownMenuItem className="gap-2">
                          <CopyIcon className="text-muted-foreground size-4" />
                          <span>
                            {t("ticket.actions.contact.copy_contact_id")}
                          </span>
                        </DropdownMenuItem>
                      }
                    />
                    <Copy
                      content={contact.appContact.appUserId}
                      render={
                        <DropdownMenuItem className="gap-2">
                          <CopyIcon className="text-muted-foreground size-4" />
                          <span>
                            {t("ticket.actions.contact.copy_app_user_id")}
                          </span>
                        </DropdownMenuItem>
                      }
                    />
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {traits.length > 0 ? (
            <div className="mt-2 flex items-center gap-2">
              {traits.map((trait) => (
                <trait.content key={trait.id} />
              ))}
            </div>
          ) : null}

          <div className="mt-4 text-sm">
            {contact.__typename === "SupportActorEmailContact" ? (
              <div className="flex items-center">
                <Copy
                  content={contact.emailContact.email}
                  className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                >
                  {t("ticket.contact_detail.email")}
                </Copy>
                <div className="flex min-h-8 items-center truncate px-3">
                  <Tooltip>
                    <TooltipTrigger className="truncate">
                      <span className="truncate">
                        {contact.emailContact.email}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {contact.emailContact.email}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            ) : null}
            {contact.__typename === "SupportActorAppContact" ? (
              <>
                <div className="flex items-center">
                  <Copy
                    content={contact.appContact.email ?? ""}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.email")}
                  </Copy>
                  <div className="flex min-h-8 items-center truncate px-3">
                    {contact.appContact.email ? (
                      <Tooltip>
                        <TooltipTrigger className="truncate">
                          <span className="truncate">
                            {contact.appContact.email}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {contact.appContact.email}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-muted-foreground">{t("na")}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <Copy
                    content={contact.appContact.phoneNumber ?? ""}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.phone_number")}
                  </Copy>
                  <div className="flex min-h-8 items-center px-3">
                    {contact.appContact.phoneNumber ? (
                      parsePhoneNumberFromString(
                        contact.appContact.phoneNumber,
                      )?.formatInternational()
                    ) : (
                      <span className="text-muted-foreground">{t("na")}</span>
                    )}
                  </div>
                </div>

                <div className="my-2">
                  <Separator />
                </div>

                <div className="flex items-center">
                  <Copy
                    content={
                      languageFormatter.of(
                        contact.appContact.language
                          .toString()
                          .replace("_", "-"),
                      ) ?? ""
                    }
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.language")}
                  </Copy>
                  <div className="flex min-h-8 items-center px-3 first-letter:uppercase">
                    <span className="first-letter:uppercase">
                      {languageFormatter.of(
                        contact.appContact.language
                          .toString()
                          .replace("_", "-"),
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex items-center">
                  <Copy
                    content={
                      regionFormatter.of(
                        contact.appContact.store.toUpperCase(),
                      ) ?? ""
                    }
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.delivery_area")}
                  </Copy>
                  <div className="flex min-h-8 items-center gap-2 px-3">
                    <StoreIcon
                      storeId={contact.appContact.store}
                      className="size-4"
                    />
                    {regionFormatter.of(contact.appContact.store.toUpperCase())}
                  </div>
                </div>

                <div className="flex items-center">
                  <Copy
                    content={new Date(
                      contact.appContact.latestActivityAt,
                    ).toUTCString()}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.last_activity")}
                  </Copy>
                  <div className="flex min-h-8 items-center px-3">
                    <span className="first-letter:uppercase">
                      <RelativeTime
                        dateTime={new Date(contact.appContact.latestActivityAt)}
                        options={{
                          style: "long",
                          numeric: "auto",
                        }}
                      />
                    </span>
                  </div>
                </div>

                <div className="flex items-center">
                  <Copy
                    content={contact.appContact.timezone}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.time_zone")}
                  </Copy>
                  <div className="flex min-h-8 items-center px-3">
                    <Time timezone={contact.appContact.timezone} />
                  </div>
                </div>

                <div className="my-2">
                  <Separator />
                </div>

                <div className="flex items-center">
                  <Copy
                    content={formatCurrency(
                      (primaryCredits?.balanceAmountInCents ?? 0) / 100,
                      toLocale(i18n.language),
                      {
                        currency: primaryCredits
                          ? primaryCredits.store === Store.Eu
                            ? "EUR"
                            : "USD"
                          : "EUR",
                        minimumFractionDigits: 0,
                      },
                    )}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.credits")}
                  </Copy>
                  <div className="flex min-h-8 items-center gap-2 px-3">
                    <span>
                      {formatCurrency(
                        (primaryCredits?.balanceAmountInCents ?? 0) / 100,
                        toLocale(i18n.language),
                        {
                          currency: primaryCredits
                            ? primaryCredits.store === Store.Eu
                              ? "EUR"
                              : "USD"
                            : "EUR",
                          minimumFractionDigits: 0,
                        },
                      )}
                    </span>

                    {secondaryCredits &&
                    secondaryCredits.balanceAmountInCents > 0 ? (
                      <span className="text-muted-foreground">
                        {formatCurrency(
                          secondaryCredits.balanceAmountInCents / 100,
                          toLocale(i18n.language),
                          {
                            currency:
                              secondaryCredits.store === Store.Eu
                                ? "EUR"
                                : "USD",
                            minimumFractionDigits: 0,
                          },
                        )}
                      </span>
                    ) : null}

                    {(primaryCredits?.transactions.length ?? 0) > 0 ||
                    (secondaryCredits?.transactions.length ?? 0) > 0 ? (
                      <Dialog>
                        <DialogTrigger
                          render={
                            <Button size="sm" variant="ghost">
                              <HistoryIcon className="size-4" />
                            </Button>
                          }
                        />
                        <DialogContent className="h-[60vh]">
                          <CreditSummaryDialog contact={contact.appContact} />
                        </DialogContent>
                      </Dialog>
                    ) : null}
                  </div>
                </div>

                <div className="flex items-center">
                  <Copy
                    content={contact.appContact.referralCode ?? ""}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.referral_code")}
                  </Copy>
                  <div className="flex min-h-8 items-center px-3">
                    {contact.appContact.referralCode ?? (
                      <span className="text-muted-foreground">{t("na")}</span>
                    )}
                  </div>
                </div>

                {contact.appContact.referralGodfather ? (
                  <>
                    {contact.appContact.referralGodfather.appContact
                      .referralCode ? (
                      <div className="flex items-center">
                        <Copy
                          content={
                            contact.appContact.referralGodfather.appContact
                              .referralCode
                          }
                          className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                        >
                          {t("ticket.contact_detail.referral_godfather_code")}
                        </Copy>
                        <div className="flex min-h-8 items-center px-3">
                          {
                            contact.appContact.referralGodfather.appContact
                              .referralCode
                          }
                        </div>
                      </div>
                    ) : null}

                    <div className="flex items-center">
                      <Copy
                        content={getContactFullName(
                          contact.appContact.referralGodfather,
                        )}
                        className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                      >
                        {t("ticket.contact_detail.referral_godfather_name")}
                      </Copy>
                      <Button
                        variant="ghost"
                        size="sm"
                        nativeButton={false}
                        render={
                          <Link
                            to="/contact/$contactId"
                            params={{
                              contactId:
                                contact.appContact.referralGodfather.id,
                            }}
                            target="_blank"
                          >
                            {getContactFullName(
                              contact.appContact.referralGodfather,
                            )}
                          </Link>
                        }
                      />
                    </div>
                  </>
                ) : null}

                <div className="flex items-center">
                  <Copy
                    content={contact.appContact.mobile.app.version}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.app_version")}
                  </Copy>
                  <div className="flex min-h-8 items-center px-3">
                    {contact.appContact.mobile.app.isLatestVersion ? (
                      <Tooltip>
                        <TooltipTrigger>
                          {t("ticket.contact_detail.up_to_date")}
                        </TooltipTrigger>
                        <TooltipContent>
                          {contact.appContact.mobile.app.version}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      contact.appContact.mobile.app.version
                    )}
                  </div>
                </div>

                <div className="my-2">
                  <Separator />
                </div>

                <div className="flex items-center">
                  <Copy
                    content={`${contact.appContact.mobile.device?.os ?? ""} ${contact.appContact.mobile.device?.osVersion ?? ""}`}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.os")}
                  </Copy>
                  <div className="flex min-h-8 items-center gap-2 px-3">
                    {contact.appContact.mobile.device ? (
                      <>
                        <span>
                          {contact.appContact.mobile.device.osVersion}
                        </span>
                        <span>
                          {contact.appContact.mobile.device.os ===
                          SupportAppContactMobileDeviceOs.IOs ? (
                            <SiApple className="size-4" />
                          ) : (
                            <SiAndroid className="size-4" />
                          )}
                        </span>
                      </>
                    ) : (
                      <span className="text-muted-foreground">{t("na")}</span>
                    )}
                  </div>
                </div>

                <div className="flex items-center">
                  <Copy
                    content={contact.appContact.mobile.device?.brand ?? ""}
                    className="text-muted-foreground shrink-0 basis-[92px] truncate text-left text-sm"
                  >
                    {t("ticket.contact_detail.device_brand")}
                  </Copy>
                  <div className="flex min-h-8 items-center gap-2 px-3">
                    {contact.appContact.mobile.device?.brand ?? (
                      <span className="text-muted-foreground">{t("na")}</span>
                    )}
                  </div>
                </div>

                {contact.appContact.latestShippingAddress ? (
                  <>
                    <div className="my-2">
                      <Separator />
                    </div>
                    <LatestShippingAddressInfo
                      {...contact.appContact.latestShippingAddress}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>
        <div className="bg-background w-full overflow-hidden rounded-lg border">
          <Tabs defaultValue="tickets" className="flex h-full grow flex-col">
            <TabsList className="mx-7 my-6">
              <TabsTrigger value="tickets">
                {t("contact.tabs.tickets")}
              </TabsTrigger>
              <TabsTrigger value="orders">
                {t("contact.tabs.orders")}
              </TabsTrigger>
            </TabsList>
            <TabsContent
              value="tickets"
              className="h-full w-full overflow-auto"
            >
              <TicketsTab contactId={contactId} />
            </TabsContent>
            {contact.__typename === "SupportActorAppContact" ? (
              <TabsContent
                value="orders"
                className="h-full w-full overflow-auto"
              >
                <OrdersTab customerId={contact.appContact.appUserId} />
              </TabsContent>
            ) : null}
          </Tabs>
        </div>
      </div>
      <Dialog
        open={dialogOpen === Dialogs.CreateTicket}
        onOpenChange={(open) =>
          setDialogOpen(open ? Dialogs.CreateTicket : undefined)
        }
      >
        <DialogContent>
          <CreateTicketDialog
            setIsOpen={(open) =>
              setDialogOpen(open ? Dialogs.CreateTicket : undefined)
            }
            defaultContactId={contactId}
          />
        </DialogContent>
      </Dialog>
      {contact.__typename === "SupportActorAppContact" ? (
        <>
          <Dialog
            open={dialogOpen === Dialogs.AddCredit}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.AddCredit : undefined)
            }
          >
            <DialogContent className="sm:max-w-xl">
              <AddCreditDialog
                actorId={contact.id}
                contact={contact.appContact}
                onSuccess={() => {
                  refreshContact();

                  setDialogOpen(undefined);
                }}
              />
            </DialogContent>
          </Dialog>
          <Dialog
            open={dialogOpen === Dialogs.AddCreditViaReferral}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.AddCreditViaReferral : undefined)
            }
          >
            <DialogContent className="sm:max-w-xl">
              <AddCreditViaReferralDialog
                actorId={contact.id}
                contact={contact.appContact}
                onSuccess={() => {
                  refreshContact();

                  setDialogOpen(undefined);
                }}
              />
            </DialogContent>
          </Dialog>
          <Dialog
            open={dialogOpen === Dialogs.RemoveCredit}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.RemoveCredit : undefined)
            }
          >
            <DialogContent className="sm:max-w-xl">
              <RemoveCreditDialog
                actorId={contact.id}
                contact={contact.appContact}
                onSuccess={() => {
                  refreshContact();

                  setDialogOpen(undefined);
                }}
              />
            </DialogContent>
          </Dialog>
          <Dialog
            open={dialogOpen === Dialogs.EditReferralCode}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.EditReferralCode : undefined)
            }
          >
            <DialogContent className="sm:max-w-xl">
              <EditReferralCodeDialog
                actorId={contact.id}
                contact={contact.appContact}
                onSuccess={() => {
                  refreshContact();

                  setDialogOpen(undefined);
                }}
              />
            </DialogContent>
          </Dialog>
          <AlertDialog
            open={dialogOpen === Dialogs.MarkAsFraudster}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.MarkAsFraudster : undefined)
            }
          >
            <MarkAsFraudsterDialog
              contactId={contact.id}
              onSuccess={() => {
                refreshContact();

                setDialogOpen(undefined);
              }}
            />
          </AlertDialog>
          <AlertDialog
            open={dialogOpen === Dialogs.MarkAsInfluencer}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.MarkAsInfluencer : undefined)
            }
          >
            <MarkAsInfluencerDialog
              contactId={contact.id}
              onSuccess={() => {
                refreshContact();

                setDialogOpen(undefined);
              }}
            />
          </AlertDialog>
          <AlertDialog
            open={dialogOpen === Dialogs.UnmarkAsFraudster}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.UnmarkAsFraudster : undefined)
            }
          >
            <UnmarkAsFraudsterDialog
              contactId={contact.id}
              onSuccess={() => {
                refreshContact();

                setDialogOpen(undefined);
              }}
            />
          </AlertDialog>
          <AlertDialog
            open={dialogOpen === Dialogs.UnmarkAsInfluencer}
            onOpenChange={(open) =>
              setDialogOpen(open ? Dialogs.UnmarkAsInfluencer : undefined)
            }
          >
            <UnmarkAsInfluencerDialog
              contactId={contact.id}
              onSuccess={() => {
                refreshContact();

                setDialogOpen(undefined);
              }}
            />
          </AlertDialog>
        </>
      ) : null}
    </>
  );
}

function TicketsTab({ contactId }: { contactId: string }) {
  const { t } = useTranslation();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteTicketsQuery(
    {
      filters: {
        contactId: {
          in: [contactId],
        },
      },
      sortBy: {
        field: SupportTicketsSortableFields.StatusUpdatedAt,
        direction: SupportTicketsSortableDirection.Desc,
      },
      first: 50,
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.supportTickets.pageInfo.hasNextPage
          ? {
              after: lastPage.supportTickets.pageInfo.endCursor,
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.supportTickets),
    },
  );

  const tickets =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("tickets.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <BookSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("contact.tickets.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("contact.tickets.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <TicketList
      data={tickets}
      state={{
        columnVisibility: {
          ...TICKET_DEFAULT_COLUMN_VISIBILITY,
          contact: false,
        },
        columnPinning: {
          start: ["select"],
          end: [],
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}

function OrdersTab({ customerId }: { customerId: string }) {
  const { t } = useTranslation();

  const query: Orders_Search_Input = {
    filter: {
      customerIds: [customerId],
    },
    sort: {
      field: Orders_Search_Sort_Field_Input.CreationDate,
      order: Orders_Search_Sort_Order_Input.Descending,
    },
    pageInfo: {
      first: 50,
    },
  };
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteSearchOrdersQuery(
    {
      input: {
        ...query,
      },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.orders_search.pageInfo.hasNextPage
          ? {
              input: {
                ...query,
                pageInfo: {
                  ...query.pageInfo,
                  after: lastPage.orders_search.pageInfo.endCursor,
                },
              },
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.orders_search),
    },
  );

  const orders =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("orders.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("orders.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <PackageSearchIcon />
            </EmptyMedia>
            <EmptyTitle>{t("contact.orders.search.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("contact.orders.search.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <OrderList
      data={orders}
      state={{
        ...SELECTABLE_TABLE_DEFAULT_STATE,
        columnVisibility: {
          ...ORDER_DEFAULT_COLUMN_VISIBILITY,
          recipientName: false,
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}
